/**
 * Reader preferences aligned with Al Muslim Books (`assets/books/src/index.ts` — books worker).
 * CSS variables and theme classes match `applyReaderSettings` / `saveReaderSettings` there.
 */

import type { CSSProperties } from 'react';

export const BOOKS_MODULE_READER_STORAGE_KEY = 'wi_module_books_reader_v1';
const LEGACY_MODULE_READER_KEY = 'wi_module_reader_prefs';

export type BooksReaderFontFamily = 'serif' | 'sans' | 'dyslexia' | 'system';
export type BooksReaderTheme = 'light' | 'sepia' | 'dark' | 'contrast';
export type BooksReaderMargin = 'narrow' | 'normal' | 'wide' | 'extra' | 'full';
export type BooksReaderBlockSpacing = 'compact' | 'normal' | 'spaced';

export type BooksReaderSettings = {
  font: BooksReaderFontFamily;
  theme: BooksReaderTheme;
  margin: BooksReaderMargin;
  justify: 'left' | 'center' | 'right' | 'justify';
  readerSize: number;
  lineHeight: number;
  blockSpacing: BooksReaderBlockSpacing;
  letterSpacing: 'tight' | 'normal' | 'relaxed' | 'wide';
  wordSpacing: 'normal' | 'wide';
  paragraphGap: 'compact' | 'normal' | 'relaxed' | 'airy';
  bodyWeight: 'normal' | 'medium' | 'semibold';
  hyphens: 'off' | 'auto';
  firstLineIndent: boolean;
  headingScale: 'compact' | 'normal' | 'large';
  arabicScale: 'small' | 'normal' | 'large' | 'xlarge';
  ayahLineHeight: number;
  ayahAlign: 'left' | 'center' | 'right' | 'justify';
  quranTranslation: string;
  highlightStyle: 'yellow' | 'amber' | 'green' | 'teal' | 'rose';
  linkStyle: 'default' | 'always';
  imageStyle: 'rounded' | 'soft' | 'sharp';
  showProgress: boolean;
  reduceMotion: boolean;
};

export const defaultBooksReaderSettings = (): BooksReaderSettings => ({
  font: 'serif',
  theme: 'light',
  /* Default to full-width column on large screens (Why Islam module reader) */
  margin: 'full',
  justify: 'left',
  /* Default text scale — keep comfortable but not oversized */
  readerSize: 1.05,
  lineHeight: 1.8,
  blockSpacing: 'normal',
  letterSpacing: 'normal',
  wordSpacing: 'normal',
  paragraphGap: 'normal',
  bodyWeight: 'normal',
  hyphens: 'off',
  firstLineIndent: false,
  headingScale: 'normal',
  arabicScale: 'normal',
  ayahLineHeight: 1.6,
  ayahAlign: 'left',
  quranTranslation: '20',
  highlightStyle: 'yellow',
  linkStyle: 'default',
  imageStyle: 'rounded',
  showProgress: true,
  reduceMotion: false,
});

const LINE_HEIGHT_CHOICES: { id: string; value: number }[] = [
  { id: 'tight', value: 1.5 },
  { id: 'normal', value: 1.8 },
  { id: 'relaxed', value: 2.2 },
  { id: 'loose', value: 2.5 },
];

export function lineHeightChoiceId(lineHeight: number): string {
  const found = LINE_HEIGHT_CHOICES.find((x) => x.value === lineHeight);
  return found?.id ?? 'normal';
}

export const lineHeightChoices = LINE_HEIGHT_CHOICES;

const AYAH_LINE_CHOICES: { id: string; value: number }[] = [
  { id: 'tight', value: 1.4 },
  { id: 'normal', value: 1.6 },
  { id: 'relaxed', value: 2 },
  { id: 'loose', value: 2.4 },
];

export function ayahLineChoiceId(v: number): string {
  return AYAH_LINE_CHOICES.find((x) => x.value === v)?.id ?? 'normal';
}

export const ayahLineChoices = AYAH_LINE_CHOICES;

function migrateLegacyModulePrefs(): Partial<BooksReaderSettings> | null {
  try {
    const raw = localStorage.getItem(LEGACY_MODULE_READER_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as { font?: string; lh?: string; theme?: string };
    const out: Partial<BooksReaderSettings> = {};
    if (p.font === 'sm') out.readerSize = 0.95;
    else if (p.font === 'md') out.readerSize = 1.05;
    else if (p.font === 'lg') out.readerSize = 1.15;
    if (p.lh === 'tight') out.lineHeight = 1.5;
    else if (p.lh === 'normal') out.lineHeight = 1.8;
    else if (p.lh === 'relaxed') out.lineHeight = 2.2;
    if (p.theme === 'paper') out.theme = 'light';
    else if (p.theme === 'sepia') out.theme = 'sepia';
    else if (p.theme === 'dark') out.theme = 'dark';
    return out;
  } catch {
    return null;
  }
}

export function loadBooksReaderSettings(): BooksReaderSettings {
  const base = defaultBooksReaderSettings();
  try {
    const raw = localStorage.getItem(BOOKS_MODULE_READER_STORAGE_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<BooksReaderSettings>;
      return { ...base, ...p, readerSize: clampSize(Number(p.readerSize ?? base.readerSize)) };
    }
  } catch {
    /* ignore */
  }
  const legacy = migrateLegacyModulePrefs();
  if (legacy) {
    return { ...base, ...legacy, readerSize: clampSize(Number(legacy.readerSize ?? base.readerSize)) };
  }
  return base;
}

export function saveBooksReaderSettings(s: BooksReaderSettings): void {
  try {
    localStorage.setItem(BOOKS_MODULE_READER_STORAGE_KEY, JSON.stringify(s));
  } catch {
    /* ignore */
  }
}

function clampSize(n: number): number {
  if (!Number.isFinite(n)) return 1;
  return Math.min(1.45, Math.max(0.7, n));
}

const fontMap: Record<BooksReaderFontFamily, string> = {
  serif: "'Cormorant Garamond', 'Merriweather', Georgia, serif",
  sans: "'Manrope', 'Segoe UI', system-ui, sans-serif",
  dyslexia: "'Lexend', system-ui, sans-serif",
  system: 'system-ui, -apple-system, sans-serif',
};

const marginMap: Record<BooksReaderMargin, string> = {
  narrow: 'min(100%, 640px)',
  normal: 'min(100%, 880px)',
  wide: 'min(100%, 1040px)',
  extra: 'min(100%, 1200px)',
  full: 'min(100%, min(1920px, 100vw - 40px))',
};

const hlMap: Record<BooksReaderSettings['highlightStyle'], string> = {
  yellow: 'rgba(234, 179, 8, 0.35)',
  amber: 'rgba(245, 158, 11, 0.38)',
  green: 'rgba(34, 197, 94, 0.32)',
  teal: 'rgba(45, 212, 191, 0.35)',
  rose: 'rgba(244, 63, 94, 0.22)',
};

const pgMap: Record<BooksReaderSettings['paragraphGap'], string> = {
  compact: '0.55em',
  normal: '1em',
  relaxed: '1.35em',
  airy: '1.75em',
};

const lsMap: Record<BooksReaderSettings['letterSpacing'], string> = {
  tight: '-0.02em',
  normal: '0',
  relaxed: '0.02em',
  wide: '0.05em',
};

const bwMap: Record<BooksReaderSettings['bodyWeight'], string> = {
  normal: '400',
  medium: '500',
  semibold: '600',
};

const hsMap: Record<BooksReaderSettings['headingScale'], string> = {
  compact: '0.92',
  normal: '1',
  large: '1.1',
};

const asMap: Record<BooksReaderSettings['arabicScale'], string> = {
  small: '0.88',
  normal: '1',
  large: '1.12',
  xlarge: '1.24',
};

/**
 * Full inline styles for `<article class="books-reader-content">` so typography controls always apply
 * (some browsers/React paths are flaky with custom properties alone on the host).
 */
export function booksReaderArticleStyle(s: BooksReaderSettings): CSSProperties {
  const jMap: Record<string, string> = {
    left: 'left',
    center: 'center',
    right: 'right',
    justify: 'justify',
  };
  const vars = booksReaderCssVariables(s);
  return {
    ...vars,
    /* Base column size; .md-body is boosted separately so paragraphs read larger than chrome */
    fontSize: `calc(20px * ${clampSize(s.readerSize)})`,
    lineHeight: String(s.lineHeight),
    // Keep module reader consistently Books-like serif regardless picker state.
    fontFamily: fontMap.serif,
    fontWeight: bwMap[s.bodyWeight] ?? '400',
    letterSpacing: lsMap[s.letterSpacing] ?? '0',
    wordSpacing: s.wordSpacing === 'wide' ? '0.08em' : '0',
    textAlign: (jMap[s.justify] ?? 'left') as CSSProperties['textAlign'],
    WebkitHyphens: s.hyphens === 'auto' ? 'auto' : 'none',
    hyphens: s.hyphens === 'auto' ? 'auto' : 'none',
    maxWidth: marginMap[s.margin] ?? marginMap.normal,
  } as CSSProperties;
}

/** Inline CSS variables for `.wi-books-reader-host` (inherit into `.books-reader-content`). */
export function booksReaderCssVariables(s: BooksReaderSettings): CSSProperties {
  const jMap: Record<string, string> = {
    left: 'left',
    center: 'center',
    right: 'right',
    justify: 'justify',
  };
  const ay = s.ayahAlign === 'justify' ? 'justify' : s.ayahAlign === 'center' ? 'center' : s.ayahAlign === 'right' ? 'right' : 'left';

  return {
    '--books-reader-size': String(clampSize(s.readerSize)),
    '--books-reader-line-height': String(s.lineHeight),
    '--books-reader-font': fontMap.serif,
    '--books-reader-max-width': marginMap[s.margin] ?? marginMap.normal,
    '--books-reader-justify': jMap[s.justify] ?? 'left',
    '--books-ayah-align': ay,
    '--books-ayah-line-height': String(s.ayahLineHeight),
    '--books-reader-letter-spacing': lsMap[s.letterSpacing] ?? '0',
    '--books-reader-word-spacing': s.wordSpacing === 'wide' ? '0.08em' : '0',
    '--books-reader-p-gap': pgMap[s.paragraphGap] ?? '1em',
    '--books-reader-font-weight': bwMap[s.bodyWeight] ?? '400',
    '--books-reader-hyphens': s.hyphens === 'auto' ? 'auto' : 'none',
    '--books-reader-heading-scale': hsMap[s.headingScale] ?? '1',
    '--books-arabic-relative': asMap[s.arabicScale] ?? '1',
    '--books-highlight-bg': hlMap[s.highlightStyle] ?? hlMap.yellow,
  } as CSSProperties;
}

export function booksReaderHostClassNames(s: BooksReaderSettings): string {
  const parts = ['wi-books-reader-host'];
  if (s.theme === 'dark') parts.push('books-reader-dark');
  else if (s.theme === 'sepia') parts.push('books-reader-sepia');
  else if (s.theme === 'contrast') parts.push('books-reader-contrast');
  if (s.firstLineIndent) parts.push('books-reader-indent-paragraphs');
  if (s.linkStyle === 'always') parts.push('books-reader-link-always');
  if (s.imageStyle === 'soft') parts.push('books-reader-img-soft');
  if (s.imageStyle === 'sharp') parts.push('books-reader-img-sharp');
  if (s.reduceMotion) parts.push('books-reader-motion-reduce');
  return parts.join(' ');
}

export function booksReaderContentClassNames(s: BooksReaderSettings): string {
  const bs =
    s.blockSpacing === 'compact' ? 'blocks-compact' : s.blockSpacing === 'spaced' ? 'blocks-spaced' : 'blocks-normal';
  return `books-reader-content wi-module-books-inner ${bs}`.trim();
}
